import { Newspaper, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

export const SUBSTACK_URL = "https://substack.com/@retirewithcertainty";

/**
 * Compact pill button for site headers/navs.
 * Uses Substack orange (#FF6719) for instant brand recognition.
 */
export const SubstackHeaderButton = ({ className = "", label = "Newsletter" }) => (
  <a
    href={SUBSTACK_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="substack-header-link"
    className={`inline-flex items-center gap-2 bg-[#FF6719] hover:bg-[#e55a12] text-white px-4 py-2 md:px-5 md:py-2.5 text-sm font-semibold rounded-full transition-colors ${className}`}
  >
    <Newspaper className="w-4 h-4" />
    <span className="hidden sm:inline">{label}</span>
    <span className="sm:hidden">Sub</span>
  </a>
);

/**
 * Text link for footers (styled to blend with the footer's link palette).
 */
export const SubstackFooterLink = ({ className = "" }) => (
  <a
    href={SUBSTACK_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="substack-footer-link"
    className={`flex items-center gap-2 text-white/80 hover:text-[#FF6719] transition-colors ${className}`}
  >
    <Newspaper className="w-4 h-4 text-[#FF6719]" />
    <span>Retire With Certainty — Newsletter</span>
  </a>
);

/**
 * Compact strip link for slim footers.
 */
export const SubstackSlimFooter = ({ className = "" }) => (
  <a
    href={SUBSTACK_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="substack-slim-footer-link"
    className={`hover:text-[#FF6719] transition-colors flex items-center gap-1.5 ${className}`}
  >
    <Newspaper className="w-3.5 h-3.5" />
    Newsletter
  </a>
);

/**
 * Full inline CTA card for homepage / mid-page placement.
 * Ridgemont-branded but with the Substack orange as accent.
 */
export const SubstackInlineCTA = ({ eyebrow = "The Newsletter", darkMode = false }) => (
  <section
    data-testid="substack-inline-cta"
    className={darkMode ? "py-16 md:py-20 bg-[#0A192F]" : "py-16 md:py-20 bg-white"}
  >
    <div className="max-w-[900px] mx-auto px-4 md:px-8">
      <div className="relative border-l-4 border-[#FF6719] bg-gradient-to-r from-[#FAFAF9] to-white p-8 md:p-12 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
          <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 bg-[#FF6719] flex items-center justify-center rounded-full shadow-lg">
            <Newspaper className="w-8 h-8 md:w-10 md:h-10 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-xs tracking-[0.25em] uppercase text-[#FF6719] font-semibold mb-2">
              {eyebrow}
            </p>
            <h3
              className="text-2xl md:text-3xl font-light text-[#0A192F] tracking-tight mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Retire With Certainty.
              <span className="block text-[#57534E] text-lg md:text-xl mt-1 font-normal not-italic">
                Weekly retirement thinking, delivered.
              </span>
            </h3>
            <p className="text-[#57534E] leading-relaxed text-sm md:text-base mb-5">
              Real questions from real households. Plain-English answers on Social Security timing,
              annuity decisions, tax-efficient income, and the retirement moves nobody explains
              until it's too late. Free. No spam. Unsubscribe anytime.
            </p>
            <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer">
              <Button
                className="bg-[#FF6719] hover:bg-[#e55a12] text-white px-7 py-6 text-base font-semibold"
                data-testid="substack-inline-cta-button"
              >
                <Mail className="w-5 h-5 mr-2" />
                Subscribe on Substack
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default SubstackHeaderButton;
